import { getApiInst } from '~/atoms/MangoApi'

import type {
  BulkUpdateStatusPayload,
  BulkUpdateStatusResult,
  PaginatedPhoneNumberResult,
  PhoneNumberCreatePayload,
  PhoneNumberListItem,
  PhoneNumberListParams,
  PhoneNumbersApi,
  PhoneNumberUpdatePayload,
  SelectOption,
} from '@mangovoice/phone-numbers-ui'

type ApiFieldErrors = Record<string, string[]>
type SimpleResponse<T> = { ok: boolean; data?: T; errors?: ApiFieldErrors }

interface ApiErrorParameter {
  name: string
  message: string
  code?: string
}

function extractFieldErrors(data: unknown): ApiFieldErrors | undefined {
  if (!data || typeof data !== 'object') return undefined

  const body = data as Record<string, unknown>

  if (Array.isArray(body.parameters)) {
    const errors: ApiFieldErrors = {}
    for (const param of body.parameters as ApiErrorParameter[]) {
      if (param.name && param.message) {
        if (!errors[param.name]) errors[param.name] = []
        errors[param.name].push(param.message)
      }
    }
    return Object.keys(errors).length > 0 ? errors : undefined
  }

  return data as ApiFieldErrors
}

function wrapApiCall<T>(
  call: () => Promise<{ ok: boolean; data?: unknown }>,
): Promise<SimpleResponse<T>> {
  return call().then((response) => {
    if (response.ok) {
      return { ok: true, data: (response.data as T) ?? undefined }
    }
    return {
      ok: false,
      data: undefined,
      errors: extractFieldErrors(response.data),
    }
  })
}

function buildQueryString(params: Record<string, unknown>): string {
  const parts: string[] = []
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      parts.push(
        `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`,
      )
    }
  }
  return parts.join('&')
}

interface RawSelectItem {
  id: number
  name?: string
  display?: string
  key?: string
  number?: string
  uuid?: string
  type?: string
  active?: boolean
}

interface PaginatedResult<T> {
  count: number
  results: T[]
  next: string | null
  previous: string | null
}

function normalizeSelectOptions(items: RawSelectItem[]): SelectOption[] {
  return items.map((item) => ({
    ...item,
    name: item.name || item.display || item.key || String(item.id),
  }))
}

function wrapSelectCall(
  call: () => Promise<{ ok: boolean; data?: unknown }>,
): Promise<SimpleResponse<SelectOption[]>> {
  return call().then((response) => {
    if (response.ok && response.data) {
      const data = response.data as
        PaginatedResult<RawSelectItem> | RawSelectItem[]
      const raw = Array.isArray(data) ? data : (data.results ?? [])
      return { ok: true, data: normalizeSelectOptions(raw) }
    }
    return { ok: false, data: undefined }
  })
}

function transport() {
  return getApiInst().getHttpTransport()
}

export const phoneNumberApi: PhoneNumbersApi = {
  getPhoneNumbers: (params: PhoneNumberListParams) => {
    const qs = buildQueryString({
      pbx_id: params.pbx_id,
      limit: params.limit,
      offset: params.offset,
      search: params.search,
      ordering: params.ordering,
      status: params.status,
      type: params.type,
      voice_provider: params.voice_provider,
      sms_provider: params.sms_provider,
      toll_free: params.toll_free,
      local_presence: params.local_presence,
      number_type: params.number_type,
      feature: params.feature,
      deleted: params.deleted,
    })
    const url = qs ? `/app/v1/phone-numbers/?${qs}` : '/app/v1/phone-numbers/'
    return wrapApiCall<PaginatedPhoneNumberResult>(() => transport().get(url))
  },

  getPhoneNumber: (id: number, options?: { includeDiagnostics?: boolean }) => {
    const params = ['deleted=true']
    if (options?.includeDiagnostics) params.push('include_diagnostics=true')
    return wrapApiCall<PhoneNumberListItem>(() =>
      transport().get(`/app/v1/phone-numbers/${id}/?${params.join('&')}`),
    )
  },

  createPhoneNumber: (payload: PhoneNumberCreatePayload) =>
    wrapApiCall<PhoneNumberListItem>(() =>
      transport().post('/app/v1/phone-numbers/', payload),
    ),

  updatePhoneNumber: (id: number, payload: PhoneNumberUpdatePayload) =>
    wrapApiCall<PhoneNumberListItem>(() =>
      transport().patch(`/app/v1/phone-numbers/${id}/`, payload),
    ),

  deletePhoneNumber: (id: number) =>
    wrapApiCall(() => transport().delete(`/app/v1/phone-numbers/${id}/`)),

  restorePhoneNumber: (id: number) =>
    wrapApiCall<PhoneNumberListItem>(() =>
      transport().post(`/app/v1/phone-numbers/${id}/restore/`),
    ),

  bulkUpdateStatus: async (
    payload: BulkUpdateStatusPayload,
  ): Promise<SimpleResponse<BulkUpdateStatusResult>> => {
    const response = await transport().post<BulkUpdateStatusResult>(
      '/app/v1/phone-numbers/bulk-update-status/',
      payload,
    )
    const data = response.ok ? response.data : (response.data as unknown)
    if (data && typeof data === 'object' && 'success_count' in data) {
      return { ok: response.ok, data: data as BulkUpdateStatusResult }
    }
    return { ok: false, data: undefined }
  },

  noopSave: (id: number) =>
    wrapApiCall<PhoneNumberListItem>(() =>
      transport().put(`/app/v1/phone-numbers/${id}/noop-save/`),
    ),

  getStatuses: () =>
    wrapSelectCall(() => transport().get('/app/v1/phone-number-statuses/')),

  getProviders: () =>
    wrapSelectCall(() => transport().get('/app/v1/providers/')),

  getExtensions: async (pbxId: number) => {
    const response = await transport().get<
      PaginatedResult<RawSelectItem> | RawSelectItem[]
    >(`/app/v1/extensions/select/?pbx_id=${pbxId}&limit=1000`)
    if (!response.ok || !response.data) {
      return { ok: false, data: undefined }
    }
    const data = response.data
    const raw = Array.isArray(data) ? data : (data.results ?? [])
    const EXCLUDED_EXTENSION_TYPES = new Set([
      'ExtensionRingGroup',
      'ExtensionCallQueue',
      'ExtensionSystem',
    ])
    const filtered = raw.filter((item) => {
      if (item.active === false) return false
      return !item.type || !EXCLUDED_EXTENSION_TYPES.has(item.type)
    })
    return { ok: true, data: normalizeSelectOptions(filtered) }
  },

  getFaxMachines: (pbxId: number) =>
    wrapSelectCall(() =>
      transport().get(`/app/v1/pbxes/${pbxId}/virtual-fax-machines-select/`),
    ),

  getIntegrations: (pbxId: number) =>
    wrapSelectCall(() =>
      transport().get(`/app/v1/pbxes/${pbxId}/integrations-select/`),
    ),
}
